import React from "react";
import { BookOpen, FileText, Search, RefreshCw, CheckCircle2, ArrowRight, ShieldCheck, Clock, Target, Users, Scale, FileSearch, PenTool, Shield, Globe } from "lucide-react";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { usePopup } from "../context/PopupContext";
import SectionCarousel from "../components/SectionCarousel";
import CTABanner from "../components/CTABanner";
import TestimonialCarousel from "../components/TestimonialCarousel";
import PageMeta from "../components/PageMeta";

/**
 * Full detail for every service we offer. Rendered as one dedicated section
 * per service, alternating left/right visual composition.
 */
const SERVICE_DETAILS = [
  {
    slug: "creation",
    num: "01",
    icon: BookOpen,
    title: "Wiki Creation & Setup",
    tagline: "A complete Wikipedia page — from a blank page to a defended publication.",
    paragraphs: [
      "This is our flagship engagement: an end-to-end build of a new Wikipedia page for an individual or company. A senior Wikipedian owns the project from the first call to the moment your article is accepted, live and stable.",
      "We begin with a written notability audit against WP:GNG, WP:BIO and WP:NCORP. Only after you pass that bar do we move into deep source research, structured drafting in neutral-tone wikitext, and submission through Articles for Creation (AfC). Reviewer feedback is handled for you — no direct interaction required.",
    ],
    included: [
      "Written notability eligibility audit",
      "Dedicated senior editor",
      "Source compilation (20–40 references)",
      "Neutral-tone wikitext drafting",
      "Two revision rounds with you",
      "AfC submission handled end-to-end",
      "Reviewer dialogue on your behalf",
      "Post-publish stabilisation watch (30 days)",
    ],
    deliverables: [
      "Notability report (PDF)",
      "Full wikitext draft",
      "Citations library (Drive)",
      "AfC receipt + tracking link",
      "Live URL on publication",
    ],
    timeline: "4–10 weeks",
    bestFor: "First pages for founders, authors, executives, public figures and companies.",
    cta: "Start a new page",
  },
  {
    slug: "writing",
    num: "02",
    icon: FileText,
    title: "Content Writing & Editing",
    tagline: "Rescue a draft, rewrite a reverted page, or upgrade a weak article.",
    paragraphs: [
      "Already have a draft or an existing page that keeps getting reverted? Our editorial desk steps in where the writing went wrong — rewriting promotional copy into encyclopedic prose, upgrading weak citations with reliable secondary sources, and bringing the structure in line with what reviewers expect.",
      "You send us the current version, the sources you have, and any specific concerns flagged by reviewers. We return a redlined draft with rationale for every change, then polish until it's ready to survive the next review cycle.",
    ],
    included: [
      "Tone-first rewrite to WP:NPOV",
      "Citation audit and upgrade",
      "Structure and section reorganisation",
      "Redlined draft with inline rationale",
      "Compatible-with-your-voice editing",
      "Pre-submission policy check",
      "Two revision rounds",
      "Optional AfC resubmission",
    ],
    deliverables: [
      "Annotated redline document",
      "Cleaned-up wikitext",
      "Citation replacement map",
      "Editor's notes on next steps",
    ],
    timeline: "2–5 weeks",
    bestFor: "Existing drafts, deleted pages with rescue potential, or live pages needing an overhaul.",
    cta: "Fix my draft",
  },
  {
    slug: "compliance",
    num: "03",
    icon: Search,
    title: "Compliance & Notability Assessment",
    tagline: "Before you spend a dollar — a written report on whether Wikipedia will accept you.",
    paragraphs: [
      "The single biggest reason Wikipedia pages fail is being attempted before the subject is eligible. Our compliance service is a stand-alone, diagnostic engagement: a structured written review of your press footprint, public record and existing references measured against Wikipedia's actual notability policies.",
      "You receive an honest verdict — eligible, borderline, or not-yet — backed by a detailed source gap analysis. If you're not ready, we outline a PR and publication roadmap that will move you across the threshold over the next 6–18 months.",
    ],
    included: [
      "WP:GNG / WP:BIO / WP:NCORP assessment",
      "Independent source evaluation",
      "Reliable-source hierarchy mapping",
      "Risk rating (Low / Medium / High)",
      "Source gap analysis",
      "Remediation roadmap (if not yet eligible)",
      "Full refund policy if not notable",
      "30-minute review call with the auditor",
    ],
    deliverables: [
      "Written notability verdict (PDF)",
      "Source spreadsheet with tier ratings",
      "Recommended sources to pursue",
      "Roadmap with 6/12/18-month milestones",
    ],
    timeline: "3–5 business days",
    bestFor: "Anyone unsure whether Wikipedia will accept them, or planning a PR strategy with Wikipedia as the endgame.",
    cta: "Request an audit",
  },
  {
    slug: "maintenance",
    num: "04",
    icon: RefreshCw,
    title: "Wiki Maintenance, Updates & Translation",
    tagline: "Keep your page accurate, defended and globally available — long after it goes live.",
    paragraphs: [
      "A Wikipedia page is not static. Facts change, news breaks, competitors and critics edit, and occasionally vandals appear. Our maintenance service monitors your live page 24/7, implements quarterly content refreshes as your story evolves, and steps in to revert unwanted edits whenever they happen.",
      "The same engagement covers cross-language republication. Once your English page is live, we translate and submit it to any of 42 active language editions of Wikipedia, each with its own native editor and community norms — so your Portuguese, Arabic or Japanese audience finds the same trustworthy record.",
    ],
    included: [
      "24/7 page watchlist monitoring",
      "Vandalism and biased-edit reverts",
      "Quarterly content refresh sprints",
      "Breaking-news updates within 48 hours",
      "Wikidata and Commons maintenance",
      "Source-link decay checks",
      "Translation to 42 Wikipedia languages",
      "Native editor assignment per language",
    ],
    deliverables: [
      "Live monitoring dashboard",
      "Monthly status report",
      "Quarterly refresh commit logs",
      "Per-language publication receipts",
    ],
    timeline: "12-month retainer (renewable)",
    bestFor: "Live pages that need ongoing protection, and brands expanding into international markets.",
    cta: "Protect my page",
  },
];

const ServiceSection = ({ s, index, onCTA }) => {
  const reversed = index % 2 === 1;
  return (
    <section
      className={`${index % 2 === 0 ? "bg-white" : "bg-slate-50"} py-20`}
      data-testid={`service-section-${s.slug}`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className={`grid gap-10 lg:grid-cols-12 lg:items-start ${reversed ? "lg:[&>div:first-child]:order-2" : ""}`}>
          {/* Left visual column */}
          <div className="lg:col-span-5">
            <div className="sticky top-24">
              <div className="flex items-center gap-4">
                <div className="grid h-14 w-14 place-items-center rounded-2xl bg-[#0A2540] text-white shadow-lg">
                  <s.icon className="h-7 w-7" />
                </div>
                <div className="font-serif text-5xl font-semibold text-emerald-500/40">{s.num}</div>
              </div>
              <h2 className="font-serif mt-5 text-3xl font-semibold leading-tight text-[#0A2540] sm:text-4xl">
                {s.title}
              </h2>
              <p className="mt-3 text-base text-slate-600">{s.tagline}</p>

              <div className="mt-6 grid gap-3 text-sm">
                <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4">
                  <Clock className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                  <div>
                    <div className="font-semibold text-[#0A2540]">Timeline</div>
                    <div className="text-slate-600">{s.timeline}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4">
                  <Target className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                  <div>
                    <div className="font-semibold text-[#0A2540]">Best for</div>
                    <div className="text-slate-600">{s.bestFor}</div>
                  </div>
                </div>
              </div>

              <Button
                onClick={onCTA}
                className="mt-6 h-11 rounded-full px-6 text-sm font-semibold text-white"
                style={{ background: "linear-gradient(135deg,#0A2540 0%,#10B981 165%)" }}
                data-testid={`service-cta-${s.slug}`}
              >
                {s.cta} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Right content column */}
          <div className="lg:col-span-7">
            <div className="space-y-4">
              {s.paragraphs.map((p, i) => (
                <p key={i} className="text-[15px] leading-relaxed text-slate-700">{p}</p>
              ))}
            </div>

            <div className="mt-8 grid gap-5 md:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 bg-white p-6">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-emerald-600">
                  <ShieldCheck className="h-4 w-4" /> What's included
                </div>
                <ul className="mt-4 space-y-2 text-sm text-slate-700">
                  {s.included.map((b) => (
                    <li key={b} className="flex items-start gap-2">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-6">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-emerald-600">
                  <FileSearch className="h-4 w-4" /> Deliverables
                </div>
                <ul className="mt-4 space-y-2 text-sm text-slate-700">
                  {s.deliverables.map((d) => (
                    <li key={d} className="flex items-start gap-2">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                      {d}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default function Services() {
  const { openPopup } = usePopup();
  return (
    <div className="overflow-hidden" data-testid="services-page">
      <PageMeta
        title="Services — Wikipedia Creation, Editing, Compliance & Maintenance"
        description="End-to-end Wikipedia services: page creation & setup, content writing & editing, compliance & notability assessment, and ongoing maintenance, updates and translation in 42 languages."
        keywords="wikipedia creation service, wikipedia editing, wikipedia maintenance, wikipedia translation, notability assessment, wikipedia compliance, wikipedia page writing"
      />

      {/* 1. Hero */}
      <section className="mwb-hero-bg px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">Wikipedia services</span>
          <h1 className="font-serif mt-5 text-4xl font-semibold leading-[1.05] text-[#0A2540] sm:text-5xl lg:text-6xl">Every Wikipedia need — covered by one expert team.</h1>
          <p className="mt-5 text-base text-slate-600 sm:text-lg">From first idea to long-term defence, choose the service that matches where you are in your Wikipedia journey.</p>
        </div>
      </section>

      {/* 1b. Informative intro */}
      <section className="mx-auto max-w-7xl px-4 pt-12 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-5">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">The full picture</p>
            <h2 className="font-serif mt-3 text-3xl font-semibold leading-tight text-[#0A2540] sm:text-4xl">One engagement. Every Wikipedia stage covered.</h2>
            <p className="mt-4 text-base text-slate-600">Most agencies only handle the writing. We handle the complete lifecycle — eligibility review, research, drafting, AfC submission, reviewer dialogue, post-publication monitoring and quarterly updates — so you never have to re-start the process with someone new.</p>
            <p className="mt-3 text-base text-slate-600">Whether you need a first page, a rescue of an existing draft, or year-round monitoring of a live page, the scope is defined upfront and billed as a flat engagement.</p>
          </div>
          <div className="lg:col-span-7 grid gap-4 sm:grid-cols-3">
            {[
              { n: "Create", d: "Brand-new pages from notability review to live publication." },
              { n: "Improve", d: "Rescue drafts, upgrade weak sourcing, neutralise promotional tone." },
              { n: "Protect", d: "24/7 monitoring, vandalism defence and quarterly refreshes." },
            ].map((s) => (
              <div key={s.n} className="rounded-2xl border border-slate-200 bg-white p-5">
                <div className="font-serif text-lg text-[#0A2540]">{s.n}</div>
                <p className="mt-2 text-sm text-slate-600">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 2. Services quick-links index */}
      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8" data-testid="services-index">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {SERVICE_DETAILS.map((s) => (
            <a
              key={s.slug}
              href={`#service-${s.slug}`}
              className="group flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-5 transition-all hover:-translate-y-0.5 hover:border-emerald-200 hover:shadow-[0_16px_36px_-20px_rgba(16,185,129,0.4)]"
              data-testid={`service-link-${s.slug}`}
            >
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-emerald-600">
                {s.num} <span className="text-slate-400">/</span> <span className="text-slate-500">Service</span>
              </div>
              <div className="mt-3 flex items-center gap-3">
                <div className="grid h-10 w-10 place-items-center rounded-xl bg-[#0A2540]/5 text-[#0A2540] group-hover:bg-emerald-500 group-hover:text-white transition-colors">
                  <s.icon className="h-5 w-5" />
                </div>
                <div className="font-serif text-lg leading-tight text-[#0A2540]">{s.title}</div>
              </div>
              <p className="mt-3 flex-1 text-sm text-slate-600">{s.tagline}</p>
              <span className="mt-4 inline-flex items-center text-sm font-semibold text-emerald-600">
                Read in detail <ArrowRight className="ml-1 h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
              </span>
            </a>
          ))}
        </div>
      </section>

      {/* 3–6. DEDICATED DETAIL SECTION PER SERVICE */}
      {SERVICE_DETAILS.map((s, i) => (
        <div key={s.slug} id={`service-${s.slug}`}>
          <ServiceSection s={s} index={i} onCTA={openPopup} />
        </div>
      ))}

      {/* 7. Process */}
      <section className="bg-[#0A2540] py-20 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl"><p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-300">Process</p><h2 className="font-serif mt-3 text-3xl font-semibold sm:text-4xl">How an engagement flows.</h2></div>
          <SectionCarousel className="mt-10" items={[
            {n:"01",t:"Discovery call",d:"We learn about you, your goals and your existing press footprint."},
            {n:"02",t:"Notability audit",d:"A written report on your eligibility against Wikipedia's policies."},
            {n:"03",t:"Research & drafting",d:"Sources are compiled, the page is drafted, you approve each revision."},
            {n:"04",t:"Submission & defence",d:"We submit, respond to reviewers and defend the page after publication."},
          ]} gridCols="md:grid-cols-2 lg:grid-cols-4" renderItem={(p)=>(
            <div className="h-full rounded-2xl bg-white/5 p-6 ring-1 ring-white/10"><div className="font-serif text-4xl text-emerald-300/70">{p.n}</div><h3 className="font-serif mt-3 text-lg">{p.t}</h3><p className="mt-2 text-sm text-white/70">{p.d}</p></div>
          )}/>
        </div>
      </section>

      {/* 8. Who it's for */}
      <section className="bg-slate-50 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl"><p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">Who it's for</p><h2 className="font-serif mt-3 text-3xl font-semibold leading-tight text-[#0A2540] sm:text-4xl">We work best with…</h2></div>
          <SectionCarousel className="mt-10" items={[
            {t:"Founders & executives",d:"Backed by meaningful press and third-party recognition."},
            {t:"Authors & academics",d:"With published works or significant research contributions."},
            {t:"Artists & public figures",d:"Widely covered by independent critics or outlets."},
            {t:"Enterprises & brands",d:"With product notability, funding rounds or market leadership."},
          ]} gridCols="md:grid-cols-2 lg:grid-cols-4" renderItem={(x)=>(
            <div className="h-full rounded-2xl border border-slate-200 bg-white p-6"><h3 className="font-serif text-lg text-[#0A2540]">{x.t}</h3><p className="mt-2 text-sm text-slate-600">{x.d}</p></div>
          )}/>
        </div>
      </section>

      {/* 9. What's included across every engagement */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl"><p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">What's included</p><h2 className="font-serif mt-3 text-3xl font-semibold leading-tight text-[#0A2540] sm:text-4xl">Every engagement ships with these essentials.</h2></div>
          <SectionCarousel className="mt-10" items={[
            {n:"Dedicated editor",b:["Senior Wikipedian","Single point of contact","Weekly updates"]},
            {n:"Policy alignment",b:["WP:GNG audit","WP:NPOV review","Paid-edit disclosure"]},
            {n:"Lasting protection",b:["Page monitoring","Vandalism defence","Quarterly refresh"]},
          ]} gridCols="md:grid-cols-3" renderItem={(p)=>(
            <div className="h-full rounded-2xl border border-slate-200 bg-white p-6">
              <div className="font-serif text-2xl text-[#0A2540]">{p.n}</div>
              <ul className="mt-4 space-y-2 text-sm text-slate-700">{p.b.map(x=><li key={x} className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-500"/>{x}</li>)}</ul>
              <Button onClick={openPopup} className="mt-6 w-full rounded-full bg-[#0A2540] text-white hover:bg-[#123B6E]">Discuss scope</Button>
            </div>
          )}/>
        </div>
      </section>

      {/* 10. Service comparison */}
      <section className="bg-slate-50 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl"><p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">Compare</p><h2 className="font-serif mt-3 text-3xl font-semibold leading-tight text-[#0A2540] sm:text-4xl">Which service fits you?</h2></div>
          <div className="mt-8 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
            <table className="w-full min-w-[640px] text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="p-4">Feature</th><th className="p-4">Creation</th><th className="p-4">Editing</th><th className="p-4">Maintenance</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-slate-700">
                {[["Notability review","✓","—","—"],["Drafting","✓","✓","—"],["AfC submission","✓","—","—"],["Monitoring","Add-on","—","✓"],["Translations","Add-on","—","Add-on"]].map((row,i)=>(
                  <tr key={i}><td className="p-4 font-semibold text-[#0A2540]">{row[0]}</td>{row.slice(1).map((v,j)=><td key={j} className="p-4">{v}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* 11. Testimonials (shared) */}
      <TestimonialCarousel />

      {/* 12. FAQ */}
      <section className="bg-slate-50 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-12 lg:items-start">
            <div className="lg:col-span-7">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">FAQ</p>
              <h2 className="font-serif mt-3 text-3xl font-semibold leading-tight text-[#0A2540] sm:text-4xl">Service questions.</h2>
              <p className="mt-3 max-w-xl text-base text-slate-600">What to expect from each service — and what happens when a page is in trouble, not yet eligible, or ready to scale across languages.</p>
              <Accordion type="single" collapsible className="mt-8 space-y-3">
                {[{q:"Can you fix my deleted page?",a:"In many cases yes — we run a deletion review and rebuild the page with stronger sourcing if notability is still achievable."},
                  {q:"Do you also handle Wikidata?",a:"Yes, we handle Wikidata entities and Commons uploads as part of maintenance packages."},
                  {q:"What if I'm not notable yet?",a:"We'll tell you honestly, recommend a PR roadmap to build notability, and refund engagement fees."},
                  {q:"Do you help with Google Knowledge Panel?",a:"Yes, a clean Wikipedia page materially helps Google's Knowledge Graph — we also handle direct Knowledge Panel suggestions."}].map((i,idx)=>(
                  <AccordionItem key={idx} value={`s-${idx}`} className="overflow-hidden rounded-2xl border border-slate-200 bg-white px-6"><AccordionTrigger className="text-left text-base font-semibold text-[#0A2540]">{i.q}</AccordionTrigger><AccordionContent className="text-sm text-slate-600">{i.a}</AccordionContent></AccordionItem>
                ))}
              </Accordion>
            </div>

            <div className="lg:col-span-5">
              <div className="relative h-full min-h-[440px] overflow-hidden rounded-3xl border border-slate-200 shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=1200&q=80"
                  alt="An editor carefully reviewing a Wikipedia draft on screen"
                  className="absolute inset-0 h-full w-full object-cover"
                  loading="lazy"
                />
                <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-[#0A2540]/70 via-[#0A2540]/10 to-transparent" />
                <div className="absolute bottom-5 left-5 right-5 rounded-2xl bg-white/95 p-4 shadow-lg backdrop-blur">
                  <div className="text-xs font-semibold uppercase tracking-wider text-emerald-600">Hands-on editorial</div>
                  <div className="mt-1 font-serif text-base text-[#0A2540]">Every service is delivered by a senior Wikipedian — never outsourced.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 13. Guarantees */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="grid gap-5 md:grid-cols-3">
          {[{t:"No notability, full refund",d:"If our review concludes you're not notable, we refund the deposit — no hard feelings."},{t:"Weekly transparency",d:"You'll receive a weekly status update, every engagement, until publication."},{t:"12-month defence",d:"Signature & Enterprise include a full year of page monitoring and updates."}].map((g)=>(
            <div key={g.t} className="rounded-2xl border border-slate-200 bg-white p-6"><ShieldCheck className="h-5 w-5 text-emerald-500"/><h3 className="font-serif mt-3 text-lg text-[#0A2540]">{g.t}</h3><p className="mt-2 text-sm text-slate-600">{g.d}</p></div>
          ))}
        </div>
      </section>

      {/* 14. CTA */}
      <CTABanner title="Pick your service. Start this week." subtitle="Fill in the 30-second form and a specialist will confirm your engagement within a few hours." />
    </div>
  );
}
